import React from 'react';
import { Zap, Info, Timer } from 'lucide-react';
import type { Product } from '../../types';

interface Props {
  product: Product;
}

function fmtDateTime(iso?: string | null): string {
  if (!iso || iso.includes('2099')) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
}

/** Visualizzazione "Offerte" — fonte ERP. */
export function PromoEditor({ product }: Props) {
  const promo = product.promo;
  const hasConfig = !!promo;
  const isPermanent = promo?.isPermanent === true;
  const pct = promo?.discountPct || 0;
  const listino = product.listino10 || 0;
  const prezzoPromo = listino > 0 && pct > 0 ? listino * (1 - pct / 100) : 0;
  const risparmio = listino > 0 && prezzoPromo > 0 ? listino - prezzoPromo : 0;

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="admin-promo-editor">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <Zap className="w-4 h-4 text-amber-600" />
        <h2 className="text-sm font-semibold tracking-tight">Offerte</h2>
        {hasConfig && (
          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${isPermanent ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
            {isPermanent ? 'Permanente su ERP' : 'A tempo su ERP'}
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">% Sconto</div>
            <div className="font-semibold tabular-nums" data-testid="admin-promo-pct-value">
              {pct > 0 ? `${pct}%` : <span className="text-muted-foreground font-normal">—</span>}
            </div>
          </div>
          {prezzoPromo > 0 && (
            <div className="flex flex-col">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Prezzo promo</div>
              <div className="font-semibold text-amber-700 tabular-nums">€ {prezzoPromo.toFixed(2).replace('.', ',')}</div>
            </div>
          )}
        </div>

        {hasConfig && !isPermanent && (
          <div className="grid grid-cols-2 gap-3 text-sm" data-testid="admin-promo-window">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Inizio</div>
              <div className="font-medium">{fmtDateTime(promo?.startDate) || <span className="text-muted-foreground font-normal">—</span>}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Fine</div>
              <div className="font-medium">{fmtDateTime(promo?.endDate) || <span className="text-muted-foreground font-normal">—</span>}</div>
            </div>
          </div>
        )}

        {pct > 0 && listino > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs space-y-1">
            <div className="flex items-center gap-1.5 text-amber-900 font-semibold">
              <Info className="w-3.5 h-3.5" />
              Anteprima sconto
            </div>
            <div className="grid grid-cols-2 gap-x-4 text-amber-900">
              <div>Prezzo listino: <span className="font-semibold tabular-nums">€ {listino.toFixed(2).replace('.', ',')}</span></div>
              <div>Prezzo promo: <span className="font-semibold tabular-nums">€ {prezzoPromo.toFixed(2).replace('.', ',')}</span></div>
              <div>Sconto: <span className="font-semibold">-{Math.round(pct)}%</span></div>
              <div>Risparmio: <span className="font-semibold tabular-nums">€ {risparmio.toFixed(2).replace('.', ',')}</span></div>
            </div>
            {!isPermanent && promo?.endDate && (
              <div className="mt-2 pt-2 border-t border-amber-200 flex items-center gap-1.5 text-amber-800">
                <Timer className="w-3 h-3" />
                Scade il {fmtDateTime(promo.endDate)}
              </div>
            )}
            {isPermanent && (
              <div className="mt-2 pt-2 border-t border-amber-200 text-amber-800 font-medium">
                Offerta permanente — nessuna scadenza
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
